import type {ProjectBillableLineItem} from './schemas'

export type GroupedUnbilledJobCharges = {
    jobId: string | null
    jobName: string
    items: ProjectBillableLineItem[]
}

export type GroupedUnbilledCharges = {
    poNumber: string | null
    jobGroups: GroupedUnbilledJobCharges[]
}

export function groupUnbilledCharges(
    lineItems: ProjectBillableLineItem[],
    jobs: Array<{id: string; name: string}>,
): GroupedUnbilledCharges[] {
    const unbilled = lineItems.filter((item) => item.invoiceId === null)

    const jobMap = new Map<string, string>()
    for (const job of jobs) {
        jobMap.set(job.id, job.name)
    }

    const poGroups = new Map<string | null, ProjectBillableLineItem[]>()
    for (const item of unbilled) {
        const key = item.poNumber ?? null
        if (!poGroups.has(key)) {
            poGroups.set(key, [])
        }
        poGroups.get(key)!.push(item)
    }

    return [...poGroups.entries()].map(([poNumber, items]) => {
        const jobGroups = new Map<string | null, ProjectBillableLineItem[]>()
        for (const item of items) {
            const key = item.jobId ?? null
            if (!jobGroups.has(key)) {
                jobGroups.set(key, [])
            }
            jobGroups.get(key)!.push(item)
        }

        const orderedJobGroups: GroupedUnbilledJobCharges[] = []
        const projectItems = jobGroups.get(null) ?? []
        if (projectItems.length > 0) {
            orderedJobGroups.push({
                jobId: null,
                jobName: 'Order-level charges',
                items: projectItems,
            })
        }
        for (const job of jobs) {
            const jobItems = jobGroups.get(job.id) ?? []
            if (jobItems.length > 0) {
                orderedJobGroups.push({jobId: job.id, jobName: job.name, items: jobItems})
            }
        }
        for (const [jobId, jobItems] of jobGroups.entries()) {
            if (jobId !== null && !jobMap.has(jobId)) {
                orderedJobGroups.push({
                    jobId,
                    jobName: `Job #${jobId.substring(0, 8)}`,
                    items: jobItems,
                })
            }
        }

        return {poNumber, jobGroups: orderedJobGroups}
    })
}
